import { useEffect, useState, useRef } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  TextMessageResponseDto,
  QuoteMessageResponseDto,
  ContractMessageResponseDto,
  SystemMessageResponseDto,
  PaymentPendingMessageResponseDto,
} from "@semo/api-contracts";
import { useChatControllerGetRoom } from "@semo/api-contracts";

interface UseRealtimeChatOptions {
  roomId: string;
  supabaseClient: SupabaseClient;
  userId?: string; // Add userId for presence key

  // Callbacks
  onNewMessage?: (
    message:
      | TextMessageResponseDto
      | QuoteMessageResponseDto
      | ContractMessageResponseDto
      | SystemMessageResponseDto
      | PaymentPendingMessageResponseDto,
  ) => void;
  onMessageUpdated?: (update: { id: string; [key: string]: unknown }) => void;
  onMessageDeleted?: (data: { id: string }) => void;
  onMessageRead?: (data: {
    roomId: string;
    userId: string;
    lastReadMessageId: string;
    userType: "buyer" | "supplier";
  }) => void;

  // i18n error messages (optional)
  errorMessages?: {
    channelError?: string;
    timedOut?: string;
    closed?: string;
  };
}

/**
 * Supabase Realtime subscription hook for chat messages
 * Handles connection, event subscriptions, and cleanup
 *
 * @example
 * ```tsx
 * const { isConnected, error } = useRealtimeChat({
 *   roomId: chatId,
 *   supabaseClient: supabase,
 *   onNewMessage: (msg) => console.log('New message:', msg)
 * });
 * ```
 */
export function useRealtimeChat({
  roomId,
  supabaseClient,
  userId,
  onNewMessage,
  onMessageUpdated,
  onMessageDeleted,
  onMessageRead,
  errorMessages = {},
}: UseRealtimeChatOptions) {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthSet, setIsAuthSet] = useState(false);
  const [activeChannel, setActiveChannel] = useState<ReturnType<
    SupabaseClient["channel"]
  > | null>(null);

  // Use refs to store callbacks and avoid re-subscribing on every render
  const onNewMessageRef = useRef(onNewMessage);
  const onMessageUpdatedRef = useRef(onMessageUpdated);
  const onMessageDeletedRef = useRef(onMessageDeleted);
  const onMessageReadRef = useRef(onMessageRead);
  const errorMessagesRef = useRef(errorMessages);

  // 🔥 Channel ref to prevent re-subscription on every render
  const channelRef = useRef<ReturnType<SupabaseClient["channel"]> | null>(null);

  // Update refs when callbacks change
  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
    onMessageUpdatedRef.current = onMessageUpdated;
    onMessageDeletedRef.current = onMessageDeleted;
    onMessageReadRef.current = onMessageRead;
    errorMessagesRef.current = errorMessages;
  }, [
    onNewMessage,
    onMessageUpdated,
    onMessageDeleted,
    onMessageRead,
    errorMessages,
  ]);

  // Join room to get channel name
  const { data: joinRoomData } = useChatControllerGetRoom(roomId, {
    query: { staleTime: 1000 * 60 * 5 },
  });

  const channelName = joinRoomData?.data?.channelName;

  // ✅ Phase 1: CRITICAL - Set auth once on mount
  useEffect(() => {
    let isCancelled = false;

    (async () => {
      try {
        console.log("[useRealtimeChat] 🔐 Setting realtime auth...");
        await supabaseClient.realtime.setAuth();
        if (!isCancelled) {
          setIsAuthSet(true);
          console.log("[useRealtimeChat] ✅ Realtime auth set");
        }
      } catch (err) {
        console.error("[useRealtimeChat] ❌ Failed to set auth:", err);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [supabaseClient]);

  // ✅ Phase 2: CRITICAL - Subscribe to channel after auth is set
  useEffect(() => {
    if (!isAuthSet || !roomId || !channelName) {
      return;
    }

    // ✅ Reuse existing channel if topic matches and already connected
    if (channelRef.current?.topic === channelName && isConnected) {
      console.log(
        "[useRealtimeChat] 🔄 Reusing existing subscribed channel:",
        channelName,
      );
      return;
    }

    // Clean up old channel if switching to a different room
    if (channelRef.current) {
      console.log(
        "[useRealtimeChat] 🧹 Removing old channel:",
        channelRef.current.topic,
      );
      supabaseClient.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // ✅ Create channel synchronously (no async IIFE)
    console.log("[useRealtimeChat] 📡 Creating new channel:", channelName);
    const channel = supabaseClient
      .channel(channelName, {
        config: {
          broadcast: {
            ack: false, // No need to wait for delivery confirmation
            self: false, // Don't receive own messages
          },
          presence: {
            key: userId || roomId, // Use userId if available for unique presence key
          },
        },
      })
      .on(
        "broadcast",
        { event: "new-message" },
        (payload: {
          payload:
            | TextMessageResponseDto
            | QuoteMessageResponseDto
            | ContractMessageResponseDto
            | SystemMessageResponseDto
            | PaymentPendingMessageResponseDto;
        }) => {
          console.log("[useRealtimeChat] 📨 New message broadcast received:", {
            event: "new-message",
            messageId: payload.payload.id,
            messageType: payload.payload.messageType,
            timestamp: new Date().toISOString(),
          });
          if (onNewMessageRef.current) {
            onNewMessageRef.current(payload.payload);
          }
        },
      )
      .on(
        "broadcast",
        { event: "message-updated" },
        (payload: { payload: { id: string; [key: string]: unknown } }) => {
          console.log(
            "[useRealtimeChat] ✏️ Message updated broadcast received:",
            {
              event: "message-updated",
              messageId: payload.payload.id,
              timestamp: new Date().toISOString(),
            },
          );
          if (onMessageUpdatedRef.current) {
            onMessageUpdatedRef.current(payload.payload);
          }
        },
      )
      .on(
        "broadcast",
        { event: "message-deleted" },
        (payload: { payload: { id: string } }) => {
          console.log(
            "[useRealtimeChat] 🗑️ Message deleted broadcast received:",
            {
              event: "message-deleted",
              messageId: payload.payload.id,
              timestamp: new Date().toISOString(),
            },
          );
          if (onMessageDeletedRef.current) {
            onMessageDeletedRef.current(payload.payload);
          }
        },
      )
      .on(
        "broadcast",
        { event: "message-read" },
        (payload: {
          payload: {
            roomId: string;
            userId: string;
            lastReadMessageId: string;
            userType: "buyer" | "supplier";
          };
        }) => {
          console.log("[useRealtimeChat] 👁️ Message read broadcast received:", {
            event: "message-read",
            userId: payload.payload.userId,
            userType: payload.payload.userType,
            timestamp: new Date().toISOString(),
          });
          if (onMessageReadRef.current) {
            onMessageReadRef.current(payload.payload);
          }
        },
      )
      .subscribe((status) => {
        console.log("[useRealtimeChat] 🔌 Subscription status changed:", {
          status,
          channelName,
          timestamp: new Date().toISOString(),
        });

        if (status === "SUBSCRIBED") {
          setIsConnected(true);
          setError(null);
          console.log(
            "[useRealtimeChat] ✅ Successfully subscribed to channel:",
            channelName,
          );
        } else if (status === "CHANNEL_ERROR") {
          setIsConnected(false);
          setError(
            errorMessagesRef.current.channelError || `Connection channel error`,
          );
          console.error(
            "[useRealtimeChat] ❌ Channel error on channel:",
            channelName,
          );
        } else if (status === "TIMED_OUT") {
          setIsConnected(false);
          setError(errorMessagesRef.current.timedOut || `Connection timed out`);
          console.error("[useRealtimeChat] ⏱️ Channel timed out:", channelName);
        } else if (status === "CLOSED") {
          setIsConnected(false);
          setError(errorMessagesRef.current.closed || `Connection closed`);
          console.warn("[useRealtimeChat] 🔚 Channel closed:", channelName);
        }
      });

    channelRef.current = channel;
    setActiveChannel(channel);
    console.log(
      "[useRealtimeChat] 📡 New channel created and stored:",
      channelName,
    );
    console.log("[useRealtimeChat] Current channel ref:", channelRef.current);
    // Cleanup on unmount or channel change
    return () => {
      if (channelRef.current) {
        console.log(
          "[useRealtimeChat] 🧹 Cleaning up channel on unmount:",
          channelRef.current.topic,
        );
        supabaseClient.removeChannel(channelRef.current);
        channelRef.current = null;
        setActiveChannel(null);
      }
    };
  }, [isAuthSet, roomId, channelName, supabaseClient]);

  return { isConnected, error, channel: activeChannel };
}
